import { useForm, FieldData, TextField } from 'vopay';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

export type ChangePasswordFormValues = {
  NewPassword: string;
  ConfirmNewPassword: string;
};

const initialValues: ChangePasswordFormValues = {
  NewPassword: '',
  ConfirmNewPassword: '',
};

type Options = {
  onSubmit: (values: ChangePasswordFormValues) => Promise<void>;
};

export default function useChangePasswordFormConfig({ onSubmit }: Options) {
  const config = useConfig();

  const { renderFormElements, ctx } = useForm<ChangePasswordFormValues>({
    initialValues,
    config,
    onSubmit,
  });

  return {
    ctx,
    renderFormElements: () => renderFormElements({ config }),
  };
}

function useConfig(): FieldData<ChangePasswordFormValues>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.List.ChangePasswordDialog' });

  const newPasswordLabel = t('newPasswordLabel', { defaultValue: 'New Password' });
  const confirmPasswordLabel = t('confirmPasswordLabel', { defaultValue: 'Confirm New Password' });
  const enterPlaceholder = t('enterPlaceholder', { defaultValue: 'Enter' });

  return [
    {
      fieldName: 'NewPassword',
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().label(newPasswordLabel).required(),
      label: () => newPasswordLabel,
      renderField: ({ getFormikData }) => (
        <TextField
          {...getFormikData<ChangePasswordFormValues['NewPassword']>({})}
          preset="password"
          variant="outlineShadow"
          placeholder={enterPlaceholder}
        />
      ),
    },
    {
      fieldName: 'ConfirmNewPassword',
      getInputState: () => 'required',
      showField: () => true,
      validate: (ctx) =>
        Yup.string()
          .label(confirmPasswordLabel)
          .required()
          .oneOf([ctx.values.NewPassword], t('passwordsDontMatch', { defaultValue: 'Passwords do not match.' })),
      label: () => confirmPasswordLabel,
      renderField: ({ getFormikData }) => (
        <TextField
          {...getFormikData<ChangePasswordFormValues['ConfirmNewPassword']>({})}
          preset="password"
          variant="outlineShadow"
          placeholder={enterPlaceholder}
        />
      ),
    },
  ];
}
